import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Divider,
  InputNumber,
  Popconfirm,
  Space,
  Upload,
  message,
} from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  UploadOutlined,
  UpOutlined,
} from "@ant-design/icons";

import { imagesApi, workImagesApi } from "../services/api";
import type { WorkImage } from "../types";

export function WorkImagesManager({
  workId,
  readOnly,
}: {
  workId: string;
  readOnly: boolean;
}) {
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["work-images", workId],
    queryFn: async () => {
      const items = (await workImagesApi.list(workId)) as WorkImage[];
      return [...items].sort((a, b) => a.position - b.position);
    },
    enabled: Boolean(workId),
  });

  const images = useMemo(() => data || [], [data]);

  const addMutation = useMutation({
    mutationFn: (payload: { image_id: string }) =>
      workImagesApi.add(workId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-images", workId] });
      queryClient.invalidateQueries({ queryKey: ["work", workId] });
      message.success("Dodano obrazek");
    },
    onError: () => message.error("Nie udało się dodać obrazka"),
  });

  const reorderMutation = useMutation({
    mutationFn: (imageIds: string[]) => workImagesApi.reorder(workId, imageIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-images", workId] });
      queryClient.invalidateQueries({ queryKey: ["work", workId] });
    },
    onError: () => message.error("Nie udało się zapisać kolejności"),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { imageId: string; position: number }) =>
      workImagesApi.update(workId, payload.imageId, {
        position: payload.position,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-images", workId] });
      queryClient.invalidateQueries({ queryKey: ["work", workId] });
    },
    onError: () => message.error("Nie udało się zmienić pozycji"),
  });

  const deleteMutation = useMutation({
    mutationFn: (imageId: string) => workImagesApi.delete(workId, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-images", workId] });
      queryClient.invalidateQueries({ queryKey: ["work", workId] });
      message.success("Usunięto obrazek");
    },
    onError: () => message.error("Nie udało się usunąć"),
  });

  const move = (index: number, delta: -1 | 1) => {
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= images.length) return;

    const next = [...images];
    const tmp = next[index];
    next[index] = next[nextIndex];
    next[nextIndex] = tmp;

    reorderMutation.mutate(next.map((x) => x.image_id));
  };

  return (
    <Card loading={isLoading} style={{ marginTop: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <strong>Dodatkowe obrazki</strong>
        {!readOnly && (
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={async (file) => {
              try {
                setIsUploading(true);
                const uploaded = await imagesApi.upload(file);
                addMutation.mutate({ image_id: uploaded.id });
              } catch (e) {
                console.error(e);
                message.error("Nie udało się przesłać obrazka");
              } finally {
                setIsUploading(false);
              }
              return false;
            }}
          >
            <Button
              icon={<UploadOutlined />}
              loading={isUploading}
              disabled={readOnly}
            >
              Dodaj obrazek
            </Button>
          </Upload>
        )}
      </div>

      <Divider style={{ margin: "16px 0" }} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: 16,
        }}
      >
        {images.map((wi, idx) => {
          const src = wi.image_id
            ? imagesApi.getFileUrl(wi.image_id)
            : undefined;
          return (
            <div
              key={wi.image_id}
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: 6,
                padding: 12,
              }}
            >
              <div
                style={{
                  height: 160,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {src ? (
                  <img
                    src={src}
                    alt="work image"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <div style={{ color: "#999" }}>Brak podglądu</div>
                )}
              </div>

              <div style={{ marginTop: 12 }}>
                <Space
                  style={{ width: "100%", justifyContent: "space-between" }}
                >
                  <span style={{ color: "#666" }}>Pozycja</span>
                  <InputNumber
                    size="small"
                    value={wi.position}
                    min={0}
                    disabled={readOnly}
                    onChange={(v) => {
                      const pos = typeof v === "number" ? v : 0;
                      updateMutation.mutate({
                        imageId: wi.image_id,
                        position: pos,
                      });
                    }}
                  />
                </Space>
              </div>

              {!readOnly && (
                <div style={{ marginTop: 12 }}>
                  <Space>
                    <Button
                      size="small"
                      icon={<UpOutlined />}
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0 || reorderMutation.isPending}
                    />
                    <Button
                      size="small"
                      icon={<DownOutlined />}
                      onClick={() => move(idx, 1)}
                      disabled={
                        idx === images.length - 1 || reorderMutation.isPending
                      }
                    />
                    <Popconfirm
                      title="Usunąć obrazek?"
                      okText="Usuń"
                      cancelText="Anuluj"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteMutation.mutate(wi.image_id)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default WorkImagesManager;
